if (!customElements.get('freight-estimate-module')) {
  class FreightEstimateModule extends HTMLElement {
    constructor() {
      super();
      this.zoneRates = this.parseZoneRates(this.dataset.zoneRates);
      this.reverseGeocodeUrl = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
    }

    connectedCallback() {
      this.submitBtn = this.querySelector('[data-freight-zip-submit]');
      this.locationBtn = this.querySelector('[data-freight-location]');
      this.zipInput = this.querySelector('[data-freight-zip-input]');
      this.stateInput = this.querySelector('[data-freight-state-input]');
      this.resultEl = this.querySelector('[data-freight-zip-result]');
      this.rateEl = this.querySelector('[data-freight-rate]');
      this.methodEl = this.querySelector('[data-freight-method]');
      this.windowEl = this.querySelector('[data-freight-window]');
      this.liveRates = this.dataset.liveRates === 'true';

      this.updateDeliveryTimeline();

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      }
      if (this.locationBtn) {
        this.locationBtn.addEventListener('click', this.onUseLocation.bind(this));
      }
      if (this.zipInput) {
        this.zipInput.addEventListener('input', this.cacheAddress.bind(this));
      }
      if (this.stateInput) {
        this.stateInput.addEventListener('input', this.cacheAddress.bind(this));
      }

      // A ZIP submitted elsewhere on the page (PDP, cart drawer, cart page) already
      // resolved a rate for this visitor, so apply it here without asking again.
      const savedZip = window.localStorage.getItem('freight_zip');
      const savedState = window.localStorage.getItem('freight_state');
      if (this.stateInput && savedState) this.stateInput.value = savedState;
      if (savedZip && this.liveRates) {
        if (this.zipInput) this.zipInput.value = savedZip;
        if (savedState) this.loadLiveRate(savedZip, savedState);
      } else if (savedZip && this.zoneRates.length) {
        if (this.zipInput) this.zipInput.value = savedZip;
        this.applyZip(savedZip, false);
      }
    }

    parseZoneRates(raw) {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    resolveRate(zip) {
      const match = this.zoneRates.find((zone) =>
        Array.isArray(zone.prefixes) && zone.prefixes.some((prefix) => zip.startsWith(prefix))
      );
      return match ? match.rate : null;
    }

    formatMoney(amount) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    }

    updateDeliveryTimeline() {
      const isParcel = this.dataset.isParcel === 'true';
      if (this.methodEl) {
        this.methodEl.textContent = isParcel ? 'Parcel shipping (overnight available)' : 'Curbside pallet delivery';
      }
      if (this.windowEl) {
        this.windowEl.textContent = isParcel ? '1–2 business days; overnight available' : '3–5 business days';
      }
    }

    cacheAddress() {
      const zip = (this.zipInput?.value || '').trim();
      const state = (this.stateInput?.value || '').trim().toUpperCase();
      if (/^[0-9]{5}$/.test(zip)) {
        window.localStorage.setItem('freight_zip', zip);
      }
      if (/^[A-Z]{2}$/.test(state)) {
        window.localStorage.setItem('freight_state', state);
      }
    }

    applyZip(zip, fireEvent) {
      const quotedAmount = this.resolveRate(zip);
      if (!Number.isFinite(quotedAmount)) return;

      if (this.rateEl) {
        this.rateEl.textContent = this.formatMoney(quotedAmount) + ' freight';
      }
      if (this.resultEl) {
        this.resultEl.classList.add('freight-estimate__zip-result--visible');
        this.resultEl.textContent = 'Freight to ' + zip + ': ' + this.formatMoney(quotedAmount) + ' ' + this.dataset.deliveryMethod;
      }

      if (fireEvent) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'shipping_estimate',
          zip,
          product_id: this.dataset.productId,
          quoted_amount: quotedAmount,
        });
      }

      // Keep every freight module on the page (PDP + cart drawer + cart page) in sync.
      document.querySelectorAll('freight-estimate-module').forEach((el) => {
        if (el !== this && el.applyZip) el.applyZip(zip, false);
      });
    }

    applyLiveRate(zip, rate) {
      const amount = parseFloat(rate.price);
      if (!Number.isFinite(amount)) return;

      if (this.rateEl) {
        this.rateEl.textContent = 'Shipping starts at ' + this.formatMoney(amount);
      }
      if (this.resultEl) {
        this.resultEl.classList.add('freight-estimate__zip-result--visible');
        this.resultEl.textContent = 'Live rate to ' + zip + ': ' + this.formatMoney(amount) + ' ' + (rate.name || 'shipping');
      }
    }

    emitEstimateEvent(zip, quotedAmount) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'shipping_estimate',
        zip,
        product_id: this.dataset.productId,
        quoted_amount: quotedAmount,
      });
    }

    async loadLiveRate(zip, state) {
      if (!this.liveRates) return;

      if (this.resultEl) {
        this.resultEl.classList.add('freight-estimate__zip-result--visible');
        this.resultEl.textContent = 'Checking live shipping rates...';
      }

      try {
        const rate = await FreightEstimateModule.getLowestRate(zip, state);
        this.emitEstimateEvent(zip, parseFloat(rate.price));
        document.querySelectorAll('freight-estimate-module').forEach((el) => {
          if (el.applyLiveRate) el.applyLiveRate(zip, rate);
        });
      } catch (error) {
        if (this.resultEl) {
          this.resultEl.classList.add('freight-estimate__zip-result--visible');
          this.resultEl.textContent = 'Live shipping rates are unavailable. Rates will be confirmed at checkout.';
        }
      }
    }

    async onUseLocation() {
      if (!this.locationBtn) return;

      if (!window.isSecureContext || !navigator.geolocation) {
        this.showLocationMessage('Location detection is unavailable. Enter your ZIP code and state manually.');
        return;
      }

      this.locationBtn.disabled = true;
      this.locationBtn.textContent = 'Finding your location...';

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 300000,
          });
        });
        const address = await this.reverseGeocode(position.coords.latitude, position.coords.longitude);
        const zip = String(address.postcode || '').trim();
        const state = String(address.principalSubdivisionCode || '').replace(/^US-/, '').trim().toUpperCase();

        if (!/^[0-9]{5}$/.test(zip) || !/^[A-Z]{2}$/.test(state)) {
          throw new Error('The detected location did not include a US ZIP code and state.');
        }

        this.zipInput.value = zip;
        this.stateInput.value = state;
        this.showLocationMessage('Location found: ' + zip + ', ' + state + '. Checking shipping rates...');
        this.onSubmit();
      } catch (error) {
        const message = error.code === 1
          ? 'Location permission was not granted. Enter your ZIP code and state manually.'
          : 'We could not determine your ZIP code. Enter it and your state manually.';
        this.showLocationMessage(message);
      } finally {
        this.locationBtn.disabled = false;
        this.locationBtn.textContent = 'Use my location';
      }
    }

    async reverseGeocode(latitude, longitude) {
      const params = new URLSearchParams({
        latitude,
        longitude,
        localityLanguage: 'en',
      });
      const response = await fetch(this.reverseGeocodeUrl + '?' + params.toString());
      if (!response.ok) throw new Error('Unable to determine address');
      const address = await response.json();
      if (address.countryCode && address.countryCode !== 'US') {
        throw new Error('Shipping estimates are currently available for US addresses only.');
      }
      return address;
    }

    showLocationMessage(message) {
      if (this.resultEl) {
        this.resultEl.classList.add('freight-estimate__zip-result--visible');
        this.resultEl.textContent = message;
      }
    }

    onSubmit() {
      const zip = (this.zipInput.value || '').trim();
      const state = (this.stateInput?.value || '').trim().toUpperCase();
      if (this.stateInput) this.stateInput.value = state;
      if (!/^[0-9]{5}$/.test(zip)) {
        if (this.resultEl) {
          this.resultEl.classList.add('freight-estimate__zip-result--visible');
          this.resultEl.textContent = 'Enter a 5 digit ZIP code.';
        }
        return;
      }
      if (!/^[A-Z]{2}$/.test(state)) {
        if (this.resultEl) {
          this.resultEl.classList.add('freight-estimate__zip-result--visible');
          this.resultEl.textContent = 'Enter your two-letter state, such as NJ.';
        }
        return;
      }
      window.localStorage.setItem('freight_zip', zip);
      window.localStorage.setItem('freight_state', state);
      if (this.liveRates) {
        this.loadLiveRate(zip, state);
      } else if (this.zoneRates.length) {
        this.applyZip(zip, true);
      } else {
        this.emitEstimateEvent(zip, null);
        if (this.rateEl) {
          this.rateEl.textContent = 'Live rate available in cart';
        }
        if (this.resultEl) {
          this.resultEl.classList.add('freight-estimate__zip-result--visible');
          this.resultEl.textContent = 'ZIP saved. Add this product to your cart to see the live ShipX/FedEx rate.';
        }
      }
    }

    static async getLowestRate(zip, state) {
      const requestKey = zip + ':' + state;
      if (window.freightRateRequest?.key === requestKey) return window.freightRateRequest.promise;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const address = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'United States',
        'shipping_address[province]': state,
      });
      const prepareUrl = root + 'cart/prepare_shipping_rates.json?' + address.toString();
      const asyncUrl = root + 'cart/async_shipping_rates.json?' + address.toString();

      const promise = (async () => {
        for (let requestAttempt = 0; requestAttempt < 2; requestAttempt += 1) {
          const prepareResponse = await fetch(prepareUrl, {
            method: 'POST',
          });
          if (!prepareResponse.ok) throw new Error('Unable to prepare shipping rates');

          // Carrier rates can take several seconds to become available. A second
          // prepare cycle handles the occasional delayed or empty response.
          for (let pollAttempt = 0; pollAttempt < 20; pollAttempt += 1) {
            const response = await fetch(asyncUrl);
            if (!response.ok) throw new Error('Unable to retrieve shipping rates');
            const payload = await response.json();
            const rates = Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];
            if (rates.length) {
              const availableRates = rates
                .map((rate) => ({ ...rate, numericPrice: parseFloat(rate.price) }))
                .filter((rate) => Number.isFinite(rate.numericPrice));
              if (availableRates.length) {
                return availableRates.sort((left, right) => left.numericPrice - right.numericPrice)[0];
              }
            }
            await new Promise((resolve) => window.setTimeout(resolve, 500));
          }
        }
        throw new Error('No shipping rates returned');
      })();

      window.freightRateRequest = { key: requestKey, promise };
      return promise.finally(() => {
        if (window.freightRateRequest?.promise === promise) {
          window.freightRateRequest = null;
        }
      });
    }
  }

  customElements.define('freight-estimate-module', FreightEstimateModule);
}
